import { getBuyerId, getGuestId, getUserType } from 'dibs-cookie-jar';
import { type FC, useEffect, useCallback, useRef, Suspense } from 'react';
import { useQueryLoader, type PreloadedQuery } from 'react-relay';
import { dibsLazy } from 'dibs-lazy/exports/dibsLazy.ts';
import { CUSTOMER_TYPE_TRADE } from 'dibs-buyer-layout/exports/userStorageConstants.ts';
import { getUserSessionCountryCode } from 'dibs-regional-info/src/regionalInfoHelpers.ts';
import { useServerVarsContext } from '../../../global/ServerVarsContext/ServerVarsContext.tsx';
// components
import { SellerReviewInformationLoading } from 'dibs-seller-reviews-modal';
import VisibilityTracker from 'dibs-visibility-tracker/exports/VisibilityTracker.tsx';
import { PdpRespMarketplaceLoadingCarousel } from '../PdpRespMarketplaceLoadingCarousel/PdpRespMarketplaceLoadingCarousel.tsx';
import { SinglePersonalizedCollectionBannerLoading } from 'dibs-personalized-collections/exports/LazySinglePersonalizedCollectionBanner.ts';

// lazy components
const PdpRespMarketplaceLayoutSecondaryLazy = dibsLazy(
    'PdpRespMarketplaceLayoutSecondary',
    () =>
        import(
            /* webpackChunkName: "PdpRespMarketplaceLayoutSecondary" */ './PdpRespMarketplaceLayoutSecondary.tsx'
        )
);

// types
import {
    pdpRespMarketplaceSecondaryRootQuery,
    type pdpRespMarketplaceSecondaryRootQueryType,
    type pdpRespMarketplaceSecondaryRootQuery$variables as SecondaryRelayVariables,
} from '../../../queries/pdpRespMarketplaceSecondaryRoot.ts';

const LoadingCarousel: FC<{
    isPdpSellerReviewsVariant: boolean | undefined;
    isPersonalizedCollectionTestVariant: boolean | undefined;
}> = ({ isPdpSellerReviewsVariant, isPersonalizedCollectionTestVariant }) => {
    return (
        <>
            {isPdpSellerReviewsVariant && <SellerReviewInformationLoading />}
            <PdpRespMarketplaceLoadingCarousel />
            {isPersonalizedCollectionTestVariant && (
                <SinglePersonalizedCollectionBannerLoading isMobile={false} />
            )}
            <PdpRespMarketplaceLoadingCarousel />
            {isPersonalizedCollectionTestVariant && (
                <SinglePersonalizedCollectionBannerLoading isMobile={false} />
            )}
        </>
    );
};

export const PdpRespMarketplaceLayoutSecondaryRenderer: FC<{
    serverSecondaryQueryRef?: PreloadedQuery<pdpRespMarketplaceSecondaryRootQueryType> | null;
    relayVariables: SecondaryRelayVariables;
}> = ({ relayVariables: serverRelayVariables, serverSecondaryQueryRef }) => {
    const [secondaryQueryRef, loadSecondaryQuery] =
        useQueryLoader<pdpRespMarketplaceSecondaryRootQueryType>(
            pdpRespMarketplaceSecondaryRootQuery,
            serverSecondaryQueryRef
        );
    const relayVariables = useRef(serverRelayVariables);
    const layoutVisibilityRef = useRef<HTMLDivElement>(null);
    const {
        isBot,
        isPdpSellerReviewsVariant,
        isPdpBottomNewSlModuleTestVariant,
        isPersonalizedCollectionTestVariant,
    } = useServerVarsContext();

    useEffect(() => {
        const userId = getBuyerId(document.cookie) || '';
        const userType = getUserType(document.cookie);
        const guestId = getGuestId(document.cookie) || '';
        const userCountryCode = getUserSessionCountryCode();

        relayVariables.current = {
            ...serverRelayVariables,
            userId,
            guestId,
            userCountryCode,
            afterDisplay: true,
            afterDisplayAndBtNone: !isBot,
            fetchUser: !!userId,
            isTrade: userType === CUSTOMER_TYPE_TRADE,
            fetchSellerReviewsSection: !!isPdpSellerReviewsVariant,
            maxSponsoredItems: !isPdpBottomNewSlModuleTestVariant ? 10 : null,
            minSponsoredItemsSteps: isPdpBottomNewSlModuleTestVariant ? [5, 10, 15] : [5],
        };
    }, [isBot, serverRelayVariables, isPdpSellerReviewsVariant, isPdpBottomNewSlModuleTestVariant]);

    const loadQuery = useCallback(
        () => loadSecondaryQuery(relayVariables.current),
        [loadSecondaryQuery]
    );

    useEffect(() => {
        window.addEventListener('scroll', loadQuery);
        return () => window.removeEventListener('scroll', loadQuery);
    }, [loadQuery]);

    return (
        <>
            <VisibilityTracker
                elementRef={layoutVisibilityRef}
                onVisibilityChange={({ isVisible }) => {
                    if (isVisible) {
                        loadQuery();
                        window.removeEventListener('scroll', loadQuery);
                    }
                }}
            />
            <div ref={layoutVisibilityRef} />
            {/* secondaryQueryRef exists if initial queryRef was provided on server (for bots) or load query was called on client */}
            {secondaryQueryRef ? (
                <Suspense
                    fallback={
                        <LoadingCarousel
                            isPdpSellerReviewsVariant={isPdpSellerReviewsVariant}
                            isPersonalizedCollectionTestVariant={
                                isPersonalizedCollectionTestVariant
                            }
                        />
                    }
                >
                    <PdpRespMarketplaceLayoutSecondaryLazy
                        queryRef={secondaryQueryRef}
                        relayVariables={relayVariables.current}
                    />
                </Suspense>
            ) : (
                <LoadingCarousel
                    isPdpSellerReviewsVariant={isPdpSellerReviewsVariant}
                    isPersonalizedCollectionTestVariant={isPersonalizedCollectionTestVariant}
                />
            )}
        </>
    );
};
